/**
 * 18 Oct 2026 (D-1018-HK) — the ONE likely button on a visit row of the Housekeeping Day (the office's rows; the crew's
 * rows keep Call and their one press). Everything else the row could do goes under "More". A pure rule over facts the
 * page already computes, so it can be read and tested on its own.
 *
 * The order is the order a person would press, from the state the visit is in:
 *   1. something is with the scheduler → nothing (the "with the scheduler" line stands where the button was);
 *   2. a proposal in pencil → "Waiting for confirmation" (the schedule's own press);
 *   3. the visit is under way, or arrived, or late → Finish (End on a staff unit or an office);
 *   4. a staff unit or an office not started → Start;
 *   5. a scheduled visit someone can arrive at: the NEXT visit → Arrived; an unconfirmed one → Record the answer
 *      (confirm by phone); the rest → Arrived;
 *   6. the visit is over → Review visit; where it cannot be reviewed, Office action (Reopen) for the roles that have it;
 *   7. on hold, or scheduled with nothing to press → Office action (extend/release, the tenant phoned…); else Move;
 *   8. nothing else → Call, when there is a number.
 */
export type RowPrimary = 'none' | 'waitingConfirm' | 'finish' | 'end' | 'start' | 'arrived' | 'answer' | 'review' | 'office' | 'move' | 'call';

export interface RowFacts {
  /** The appointment's status as stored (`Scheduled`, `On Hold`, …). */
  status: string;
  /** A press (Finish, Arrived, Move…) is with the scheduler: the line stands where the buttons were. */
  waiting: boolean;
  /** The row's visit is the day's "Next" (the card under Happening now). */
  isNext: boolean;
  /** A tenant's visit whose confirmation is not `Confirmed`. */
  unconfirmed: boolean;
  /** The visit takes a completion record (Finish) — a staff unit or an office takes End. */
  closesOut: boolean;
  /** The page's own facts: the press this row would show today. */
  showFinish: boolean;
  showStart: boolean;
  canArrive: boolean;
  /** Closed out, marked completed, or over in any other way. */
  after: boolean;
  /** Review visit is for this role on this visit. */
  reviewable: boolean;
  /** The role has Office action and the visit's state allows it (the page's own test, unchanged). */
  officeAllowed: boolean;
  /** The roles that book: Waiting for confirmation on a proposal, Record the answer on an unconfirmed visit. */
  canBook: boolean;
  canMove: boolean;
  hasPhone: boolean;
}

export function primaryActionFor(f: RowFacts): RowPrimary {
  if (f.waiting) return 'none';
  if (f.status === 'Proposed') return f.canBook ? 'waitingConfirm' : f.hasPhone ? 'call' : 'none';
  if (f.showFinish) return f.closesOut ? 'finish' : 'end';
  if (f.showStart) return 'start';
  if (f.canArrive) {
    if (f.isNext) return 'arrived';
    if (f.unconfirmed && f.canBook) return 'answer';
    return 'arrived';
  }
  if (f.after) {
    if (f.reviewable) return 'review';
    if (f.officeAllowed) return 'office';
    return 'none';
  }
  if (f.officeAllowed) return 'office';
  if (f.canMove) return 'move';
  return f.hasPhone ? 'call' : 'none';
}

/** The actions a row can offer, in the order More lists them (the primary one is taken out of this list). */
export const MORE_ORDER: readonly RowPrimary[] = ['call', 'arrived', 'start', 'finish', 'end', 'answer', 'move', 'review', 'office', 'waitingConfirm'];

/**
 * What goes under More: every action the row has today, in `MORE_ORDER`, less the primary one. `available` is the set
 * the page worked out for this row (the same facts the primary rule read, as actions).
 */
export function moreActionsFor(primary: RowPrimary, available: ReadonlySet<RowPrimary>): RowPrimary[] {
  return MORE_ORDER.filter((a) => a !== primary && available.has(a));
}

/** The actions a row offers at all, from its facts — the primary one is one of these (or `none`). */
export function availableActions(f: RowFacts): Set<RowPrimary> {
  const out = new Set<RowPrimary>();
  if (f.hasPhone) out.add('call');
  if (f.waiting) return out;
  if (f.status === 'Proposed' && f.canBook) out.add('waitingConfirm');
  if (f.showFinish) out.add(f.closesOut ? 'finish' : 'end');
  if (f.showStart) out.add('start');
  if (f.canArrive) out.add('arrived');
  if (f.canArrive && f.unconfirmed && f.canBook) out.add('answer');
  if (f.canMove) out.add('move');
  if (f.after && f.reviewable) out.add('review');
  if (f.officeAllowed) out.add('office');
  return out;
}
